'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import tkns from '@lualtek/tokens/web/tokens.json';
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import styles from './glow.module.css';
export const Glow = ({ className, children, proximity = 170, spread = 80, opacity = 0, fitContent = false, borderWidth = 2, borderColor = 'var(--vibrancy-background-hard)', glowPower = 10, borderOffset = 5, innerRadius, globalHighlight = true, rainbowColors = false, glowColor, style, ...otherProps }) => {
    const containerRef = useRef(null);
    const [activeOpacity, setActiveOpacity] = useState(opacity);
    const [startingAngle, setStartingAngle] = useState('0');
    const formatRadius = useMemo(() => {
        if (!innerRadius) {
            return undefined;
        }
        if (!Array.isArray(innerRadius)) {
            return tkns.radius[innerRadius];
        }
        return innerRadius.map(r => (r !== 0 ? tkns.radius[r] : 0)).join(' ');
    }, [innerRadius]);
    const paint = useCallback((event) => {
        if (containerRef.current === null)
            return;
        // get the angle based on the center point of the card and pointer position
        // Check the card against the proximity and then start updating
        const CARD_BOUNDS = containerRef.current.getBoundingClientRect();
        // Get distance between pointer and outerbounds of card
        if (event?.x > CARD_BOUNDS.left - proximity
            && event?.x < CARD_BOUNDS.left + CARD_BOUNDS.width + proximity
            && event?.y > CARD_BOUNDS.top - proximity
            && event?.y < CARD_BOUNDS.top + CARD_BOUNDS.height + proximity) {
            // If within proximity set the active opacity
            setActiveOpacity(1);
        }
        else {
            setActiveOpacity(opacity);
        }
        const CARD_CENTER = [
            CARD_BOUNDS.left + CARD_BOUNDS.width * 0.5,
            CARD_BOUNDS.top + CARD_BOUNDS.height * 0.5,
        ];
        let ANGLE = Math.atan2(event?.y - CARD_CENTER[1], event?.x - CARD_CENTER[0]) * 180 / Math.PI;
        ANGLE = ANGLE < 0 ? ANGLE + 360 : ANGLE;
        setStartingAngle(String(ANGLE + 90));
    }, [opacity, proximity]);
    useEffect(() => {
        document.body.addEventListener('pointermove', paint);
        return () => {
            document.body.removeEventListener('pointermove', paint);
        };
    }, [paint]);
    const dynamicStyle = useMemo(() => ({
        '--active-opacity': activeOpacity,
        '--starting-angle': startingAngle,
        '--blur': 50,
        '--spread': spread,
        '--glow-color': glowColor,
        '--glow-power': `${glowPower}px`,
        '--border-offset': `${borderOffset}px`,
        '--border-width': `${borderWidth}px`,
        '--border-color': borderColor,
        '--radius': innerRadius && formatRadius,
    }), [
        activeOpacity, startingAngle, spread,
        glowPower, borderOffset, borderWidth,
        borderColor, innerRadius, glowColor, formatRadius,
    ]);
    return (_jsxs("div", { ref: containerRef, className: styles.Glow, "data-glow-global": rainbowColors ? false : globalHighlight, "data-glow-rainbow": rainbowColors, "data-glow-fit-content": fitContent, style: { ...dynamicStyle, ...style }, ...otherProps, children: [glowPower > 0 && _jsx("div", { className: styles.Light }), children] }));
};
